import React, { useState, useEffect } from "react";
import axios from "axios";

const api = axios.create({
  baseURL: "http://127.0.0.1:8000",
});

export default function ICDAD({ onDeepestICDChange, onPathChange }) {
  const [levels, setLevels] = React.useState([
    { label: "Admission Level", options: [], value: "", disabled: false, parentKey: null },
    { label: "Level 2", options: [], value: "", disabled: true, parentKey: null },
    { label: "Level 3", options: [], value: "", disabled: true, parentKey: null },
    { label: "Level 4", options: [], value: "", disabled: true, parentKey: null },
  ]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [selectedDoctor, setSelectedDoctor] = React.useState(""); // State to hold selected doctor ID

  const toTitleCase = (s = "") =>
    s
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b[a-z]/g, (c) => c.toUpperCase());

  const ensureLevel = (idx) => {
    setLevels((prev) => {
      if (idx < prev.length) return prev;
      const extra = Array.from({ length: idx - prev.length + 1 }, (_, j) => ({
        label: `Level ${prev.length + j + 1}`,
        options: [],
        value: "",
        disabled: true,
        parentKey: null,
      }));
      return [...prev, ...extra];
    });
  };

  const computePath = (lvls) =>
    lvls
      .map((L, depth) => {
        if (!L.value) return null;
        const opt = L.options.find((o) => o.key === L.value);
        return { depth, key: L.value, label: opt?.label || L.value };
      })
      .filter(Boolean);

  const deepestSelected = (lvls) => {
    for (let d = lvls.length - 1; d >= 0; d--) if (lvls[d].value) return lvls[d].value;
    return "";
  };

  const onPick = async (depth, value) => {
    setLevels((prev) => {
      const next = prev.map((x) => ({ ...x }));
      next[depth].value = value;
      for (let d = depth + 1; d < next.length; d++) {
        next[d] = { ...next[d], options: [], value: "", disabled: true, parentKey: null };
      }
      onDeepestICDChange?.(deepestSelected(next));
      onPathChange?.(computePath(next));
      return next;
    });

    if (!value) return;

    try {
      setBusy(true);
      setMsg("");
      const res = await api.get(`/api/icd/children/${encodeURIComponent(value)}/`);
      const children = res.data || [];
      if (!children.length) return;

      ensureLevel(depth + 1);
      setLevels((prev) => {
        const next = [...prev];
        const nextLvl = depth + 1;
        next[nextLvl] = {
          ...next[nextLvl],
          options: children,
          value: "",
          disabled: false,
          parentKey: value,
        };
        for (let d = nextLvl + 1; d < next.length; d++) {
          next[d] = { ...next[d], options: [], value: "", disabled: true, parentKey: null };
        }
        onDeepestICDChange?.(deepestSelected(next));
        onPathChange?.(computePath(next));
        return next;
      });
    } catch (err) {
      setMsg(`Failed to load ICD children: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Dummy list of doctors
  const doctors = [
    { id: "1", name: "Dr. John Doe" },
    { id: "2", name: "Dr. Jane Smith" },
    { id: "3", name: "Dr. Emily Johnson" },
    { id: "4", name: "Dr. Michael Brown" },
  ];

  // Function to assign the doctor and show an alert
  const handleAssignDoctor = () => {
    if (!selectedDoctor) {
      alert("Please select a doctor.");
      return;
    }

    // Show alert when doctor is assigned
    const doctor = doctors.find((doc) => doc.id === selectedDoctor);
    alert(`Patient assigned to ${doctor?.name}`);

    // Clear the selected doctor after assignment
    setSelectedDoctor("");
  };

  return (
    <section className="card">
      <div className="cardheading">
        <h2 className="title">ICD Cascade</h2>
      </div>

      {levels.map((L, depth) => (
        <div key={depth} className="row" style={{ marginBottom: 8 }}>
          <div className="label">{L.label}</div>
          <select
            className="input"
            disabled={L.disabled}
            value={L.value}
            onChange={(e) => onPick(depth, e.target.value)}
          >
            <option value="">
              {L.disabled ? "— Disabled —" : `— Select from ${toTitleCase(L.label)} —`}
            </option>
            {L.options.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ))}

      {/* Show Assign to Doctor dropdown only at the last level */}
      {levels[levels.length - 1].value && (
        <div className="row" style={{ marginBottom: 8 }}>
          <div className="label">Assign to Doctor</div>
          <select
            className="input"
            value={selectedDoctor}
            onChange={(e) => setSelectedDoctor(e.target.value)}
          >
            <option value="">— Select a Doctor —</option>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleAssignDoctor}
            style={{
              marginLeft: "10px",
              background: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 14px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Assign Doctor
          </button>
        </div>
      )}

      {busy && <div className="note">Loading…</div>}
      {msg && <div className="note">{msg}</div>}
    </section>
  );
}
